/* ---------------------------------------------------------------------------
   What this application does, and where it departs from the workbooks.

   NOT A HELP PAGE. It is the document a reviewing engineer needs before they
   will sign anything this application produces: which standard, which vehicles,
   which load factors, which of the fourteen recorded findings were corrected and
   which were reproduced as written, and what is not implemented at all. Bridge
   Watch's Guide screen exists for the same reason — an application that cannot
   explain its own arithmetic is an application nobody should trust with a
   posting sign.

   THE OPEN RULINGS ARE HERE, AT THE TOP, unresolved. Two of them change numbers
   materially and neither is ours to settle. Burying them in a footnote would be
   the application quietly picking one.
   --------------------------------------------------------------------------- */

import { h } from '../../core/dom.ts';
import { card, cardHead, fact, tag } from '../../components/widgets.ts';
import { CORRECTIONS, ENGINE_VERSION, IMPLEMENTED, allVehicles } from '../../cbplr/index.ts';
import type { State } from '../store.ts';
import { pagehead } from '../chrome.ts';

const FAMILIES: readonly { id: string; name: string; sheets: number }[] = [
  { id: 'RCFS', name: 'RC flat slab', sheets: 11 },
  { id: 'RCDG', name: 'RC deck girder (T-beam)', sheets: 11 },
  { id: 'PCDG', name: 'Prestressed concrete I-girder', sheets: 12 },
  { id: 'SIG', name: 'Steel I-girder', sheets: 7 },
  { id: 'RCDECK', name: 'RC deck slab', sheets: 6 },
];

export function methodScreen(s: Readonly<State>): HTMLElement {
  const open = CORRECTIONS.filter((c) => !c.applied);
  const applied = CORRECTIONS.filter((c) => c.applied);

  return h(
    'div',
    { class: 'rt-scr' },
    pagehead(
      s,
      `${s.profile.authority} · profile ${s.profile.version} · engine ${ENGINE_VERSION}`,
    ),

    card(
      { pad: true },
      cardHead('The standard', s.profile.name.toUpperCase()),
      h(
        'div',
        { class: 'rt-facts' },
        fact('Authority', s.profile.authority),
        fact('Rating vehicle', `${s.profile.ratingVehicle.name} · ${s.profile.ratingVehicle.tonnes} t`),
        fact('Dead load factor, LFM', String(s.profile.factors.lfm.dead)),
        fact('Live load factor, inventory', String(s.profile.factors.lfm.live.inventory)),
        fact('Live load factor, operating', String(s.profile.factors.lfm.live.operating)),
        fact('Capacity factor Uf', String(s.profile.capacityFactor)),
        fact('Modular ratio n', String(s.profile.modularRatio)),
        fact('Rating required at', s.profile.ratingRequiredAt.join(', ')),
      ),
      h(
        'div',
        { class: 'rt-tbl rt-tbl--veh' },
        h(
          'div',
          { class: 'rt-tbl__head' },
          h('span', { text: 'Vehicle' }),
          h('span', { text: 'Gross weight' }),
          h('span', { text: 'Role' }),
        ),
        ...allVehicles(s.profile).map((v) =>
          h(
            'div',
            { class: 'rt-tbl__row' },
            h('span', { text: v.name }),
            h('span', { text: `${v.tonnes} t` }),
            h('span', {
              text: v.id === s.profile.ratingVehicle.id ? 'Rating vehicle' : 'Posting vehicle',
            }),
          ),
        ),
      ),
    ),

    card(
      { pad: true, class: 'rt-alert' },
      cardHead('Open rulings', `${open.length} NOT SETTLED — AND NOT SETTLED SILENTLY`),
      h('p', {
        class: 'rt-prose',
        text:
          'Each of these is either a question for the Department or a behaviour of the 2009 workbooks that is reproduced ' +
          'deliberately. None is a task somebody forgot, and none is decided by this application on its own account.',
      }),
      h(
        'ul',
        { class: 'rt-blk rt-blk--corr' },
        ...open.map((c) =>
          h(
            'li',
            null,
            h('strong', { text: `${c.id} · ${c.family} · ${c.where}` }),
            h('span', { class: 'rt-blk__state', text: 'reproduced as written' }),
            h('p', { text: c.fault }),
            h('p', { class: 'rt-blk__effect', text: c.effect }),
          ),
        ),
      ),
    ),

    card(
      { pad: true },
      cardHead('Corrections applied', `${applied.length} DIVERGENCES FROM THE 2009 WORKBOOKS`),
      h('p', {
        class: 'rt-prose',
        text:
          'Every one is pinned by a test that asserts both values — the workbook’s and this application’s — so the ' +
          'difference is documented rather than lost. Three of them were found while reproducing the reference values ' +
          'and are not in the original study of the program.',
      }),
      h(
        'ul',
        { class: 'rt-blk rt-blk--corr' },
        ...applied.map((c) =>
          h(
            'li',
            null
            ,
            h('strong', { text: `${c.id} · ${c.family} · ${c.where}` }),
            h('span', { class: 'rt-blk__state', text: 'corrected' }),
            h('p', { text: c.workbook }),
            h('p', { class: 'rt-blk__effect', text: c.effect }),
          ),
        ),
      ),
    ),

    card(
      { pad: true },
      cardHead('What is implemented', 'THE FIVE FAMILIES OF THE 2009 PROGRAM'),
      h('p', {
        class: 'rt-prose',
        text:
          'The shared spine — the live-load library, the standards profile, the demand calculation, the rating equation ' +
          'and the posting rule — is complete and is what the remaining families are built on. Each of those is a ' +
          'capacity calculation plus a list of sections to check.',
      }),
      h(
        'div',
        { class: 'rt-tbl rt-tbl--fam' },
        h(
          'div',
          { class: 'rt-tbl__head' },
          h('span', { text: 'Family' }),
          h('span', { text: 'Excel sheets' }),
          h('span', { text: 'Simple spans' }),
          h('span', { text: 'Continuous' }),
        ),
        ...FAMILIES.map((f) => {
          const simple = IMPLEMENTED.some((i) => i.family === f.id && i.continuity === 'simple');
          return h(
            'div',
            { class: 'rt-tbl__row' },
            h('span', null, h('strong', { text: f.id }), h('span', { class: 'rt-tbl__sub', text: f.name })),
            h('span', { text: String(f.sheets) }),
            h('span', null, simple ? tag('implemented', 1) : tag('not started', 'none')),
            /* Continuous spans are not "not started" — they are a documented
               phase-1 limit. The 2009 program does not compute their demands
               either; it takes them from a separate structural analysis. */
            h('span', null, tag('demands typed in', 'none', 'As the 2009 workbooks require: DL, SDL and LL at midspan and at the pier come from a separate structural analysis.')),
          );
        }),
      ),
    ),
  );
}
